<template>
  <div>
    <!-- Hero Section -->
    <section class="relative py-16 md:py-24 bg-orange-50">
      <div class="container-custom">
        <div class="text-center max-w-3xl mx-auto">
          <img
            v-if="companyStore.logoUrl"
            :src="getImageUrl(companyStore.logoUrl)"
            :alt="companyStore.company?.name"
            class="h-16 md:h-20 w-auto object-contain mx-auto mb-6"
          >
          <h1 class="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {{ $t('home.hero.title') }}
          </h1>
          <p class="text-lg text-gray-600 mb-4">
            {{ companyStore.company?.description || $t('home.hero.subtitle') }}
          </p>

          <!-- Faixa de status: aberto/fechado, tempo de entrega, pedido mínimo -->
          <div v-if="companyStore.isLoaded" class="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 mb-8 text-sm font-medium">
            <span class="inline-flex items-center gap-1.5" :class="companyStore.company?.is_open ? 'text-green-700' : 'text-red-600'">
              <span class="w-2 h-2 rounded-full" :class="companyStore.company?.is_open ? 'bg-green-500' : 'bg-red-500'" />
              {{ companyStore.company?.is_open ? 'Aberto agora' : 'Fechado no momento' }}
            </span>
            <span class="text-gray-300">•</span>
            <span class="inline-flex items-center gap-1.5 text-gray-600">
              <ClockIcon class="w-4 h-4" />
              Entrega em ~{{ companyStore.averageDeliveryTime }} min
            </span>
            <span v-if="companyStore.minOrderValue > 0" class="text-gray-300">•</span>
            <span v-if="companyStore.minOrderValue > 0" class="inline-flex items-center gap-1.5 text-gray-600">
              Pedido mínimo {{ formatCurrency(companyStore.minOrderValue) }}
            </span>
          </div>

          <!-- Search -->
          <div class="max-w-xl mx-auto mb-8">
            <div class="relative">
              <input
                v-model="searchQuery"
                type="text"
                :placeholder="$t('home.hero.searchPlaceholder')"
                class="w-full pl-12 pr-4 py-4 rounded-full border border-gray-300 focus:ring-2 focus:ring-orange-500 focus:border-transparent shadow-lg"
                @keyup.enter="search"
              >
              <MagnifyingGlassIcon class="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <button
                @click="search"
                class="absolute right-2 top-1/2 -translate-y-1/2 px-6 py-2 rounded-full text-white font-medium transition-colors bg-orange-500 hover:bg-orange-600"
              >
                {{ $t('app.search') }}
              </button>
            </div>
          </div>
          
          <router-link
            to="/menu"
            class="inline-flex items-center px-8 py-3 rounded-full text-white font-medium text-lg transition-transform hover:scale-105 bg-orange-500 hover:bg-orange-600"
          >
            {{ $t('home.hero.cta') }}
            <ArrowRightIcon class="ml-2 h-5 w-5" />
          </router-link>
        </div>
      </div>
    </section>
    
    <!-- Categories Section -->
    <section class="py-12">
      <div class="container-custom">
        <div class="flex items-center justify-between mb-8">
          <h2 class="text-2xl font-bold text-gray-900">
            {{ $t('home.categories.title') }}
          </h2>
          <router-link
            to="/menu"
            class="text-orange-600 hover:text-orange-700 font-medium flex items-center"
          >
            {{ $t('home.categories.viewAll') }}
            <ChevronRightIcon class="h-5 w-5 ml-1" />
          </router-link>
        </div>
        
        <div v-if="loading" class="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div v-for="i in 4" :key="i" class="skeleton h-32 rounded-lg" />
        </div>
        
        <div v-else-if="categories.length > 0" class="grid grid-cols-2 md:grid-cols-4 gap-4">
          <router-link
            v-for="category in categories"
            :key="category.id"
            :to="{ path: '/menu', query: { category: category.id } }"
            class="group relative overflow-hidden rounded-lg aspect-square bg-orange-100 flex items-center justify-center"
          >
            <!-- Category Image -->
            <img
              v-if="category.image_url"
              :src="getImageUrl(category.image_url)"
              class="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
              :alt="category.name"
            >
            <!-- Fallback Gradient -->
            <div v-else class="absolute inset-0 bg-gradient-to-br from-orange-200 to-orange-300" />
            <div class="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
            <div class="absolute bottom-0 left-0 right-0 p-4">
              <h3 class="text-white font-semibold text-lg">{{ category.name }}</h3>
            </div>
          </router-link>
        </div>
        
        <div v-else class="text-center py-8 text-gray-500">
          Nenhuma categoria encontrada
        </div>
      </div>
    </section>
    
    <!-- Featured Products Section -->
    <section class="py-12 bg-white">
      <div class="container-custom">
        <div class="flex items-center justify-between mb-8">
          <h2 class="text-2xl font-bold text-gray-900">
            {{ $t('home.featured.title') }}
          </h2>
          <router-link
            to="/menu"
            class="text-orange-600 hover:text-orange-700 font-medium flex items-center"
          >
            {{ $t('home.featured.viewAll') }}
            <ChevronRightIcon class="h-5 w-5 ml-1" />
          </router-link>
        </div>
        
        <div v-if="loading" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <div v-for="i in 4" :key="i" class="skeleton h-80 rounded-lg" />
        </div>
        
        <div v-else-if="featuredProducts.length > 0" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <ProductCard
            v-for="product in featuredProducts"
            :key="product.id"
            :product="product"
          />
        </div>
        
        <div v-else class="text-center py-8 text-gray-500">
          Nenhum produto em destaque
        </div>
      </div>
    </section>
    
    <!-- How It Works Section -->
    <section class="py-12">
      <div class="container-custom">
        <h2 class="text-2xl font-bold text-gray-900 text-center mb-12">
          {{ $t('home.howItWorks.title') }}
        </h2>
        
        <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div class="text-center">
            <div class="w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center text-white bg-orange-500">
              <ShoppingBagIcon class="h-10 w-10" />
            </div>
            <h3 class="font-semibold text-lg mb-2">{{ $t('home.howItWorks.step1') }}</h3>
            <p class="text-gray-600">Navegue pelo nosso cardápio e escolha seus favoritos</p>
          </div>
          
          <div class="text-center">
            <div class="w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center text-white bg-orange-500">
              <CreditCardIcon class="h-10 w-10" />
            </div>
            <h3 class="font-semibold text-lg mb-2">{{ $t('home.howItWorks.step2') }}</h3>
            <p class="text-gray-600">Finalize seu pedido com segurança</p>
          </div>
          
          <div class="text-center">
            <div class="w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center text-white bg-orange-500">
              <TruckIcon class="h-10 w-10" />
            </div>
            <h3 class="font-semibold text-lg mb-2">{{ $t('home.howItWorks.step3') }}</h3>
            <p class="text-gray-600">Receba seu pedido no conforto da sua casa</p>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import {
  MagnifyingGlassIcon,
  ArrowRightIcon,
  ChevronRightIcon,
  ShoppingBagIcon,
  CreditCardIcon,
  TruckIcon,
  ClockIcon
} from '@heroicons/vue/24/outline'
import { useCompanyStore, useCartStore } from '@/stores'
import { categoryApi, productApi } from '@/utils/api'
import { formatCurrency } from '@/utils/formatters'
import type { Category, Product } from '@/types'
import ProductCard from '@/components/products/ProductCard.vue'

const router = useRouter()
const companyStore = useCompanyStore()
const cartStore = useCartStore()

const searchQuery = ref('')
const categories = ref<Category[]>([])
const featuredProducts = ref<Product[]>([])
const loading = ref(true)

const getImageUrl = (url: string | undefined) => {
  if (!url) return ''
  if (url.startsWith('http')) return url
  return url
}

const search = () => {
  if (searchQuery.value.trim()) {
    router.push({
      path: '/menu',
      query: { search: searchQuery.value }
    })
  }
}

const loadData = async () => {
  if (!companyStore.company?.id) {
    loading.value = false
    return
  }
  const companyId = companyStore.company.id
  
  loading.value = true
  try {
    // Carrega categorias
    const categoriesRes = await categoryApi.getByCompany(companyId)
    categories.value = categoriesRes.data
    
    // Carrega produtos em destaque
    const productsRes = await productApi.getByCompany(companyId, {
      featured_only: true,
      limit: 4
    })
    featuredProducts.value = productsRes.data
  } catch (error) {
    console.error('Erro ao carregar dados:', error)
  } finally {
    loading.value = false
  }
}

watch(() => companyStore.company?.id, (newId) => {
  if (newId) {
    loadData()
  }
}, { immediate: true })

onMounted(() => {
  cartStore.loadFromStorage()
})
</script>
